import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Smartphone, Check, AlertTriangle, Download, BookOpen, ExternalLink } from "lucide-react";
import { BottomNav } from "../components/BottomNav";
import LegalFooter from "../components/LegalFooter";
import { useI18n } from "../lib/i18n";

const ESIM_PROVIDERS = [
  {
    name: "Yesim",
    desc: "Affordable plans from $9.99 · Connects to ETECSA 4G · Supports tethering",
    descEs: "Planes desde $9.99 · Conecta a ETECSA 4G · Compatible con tethering",
    url: "https://yesim.app/country/cuba?partner_id=4678",
  },
  {
    name: "GigSky",
    desc: "Well established for Cuba · Unlimited plans available · Free 100MB trial",
    descEs: "Confiable para Cuba · Planes ilimitados · Prueba gratis 100MB",
    url: "https://www.gigsky.com/country/esim-in-cuba",
  },
  {
    name: "eSIMDB",
    desc: "Compare multiple Cuba plans side by side",
    descEs: "Compara múltiples planes para Cuba lado a lado",
    url: "https://esimdb.com/cuba",
  },
  {
    name: "GlobaleSIM",
    desc: "Flexible plans for international travelers",
    descEs: "Planes flexibles para viajeros internacionales",
    url: "https://globalesim.app/it/prodotto/cuba/",
  },
];

function SimCardIcon({ size = 20, color = "#E8B84B" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 2H9L4 7v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z" />
      <path d="M8 12h8" />
      <path d="M8 16h8" />
      <path d="M12 12v4" />
    </svg>
  );
}

export default function ConnectedPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [phoneType, setPhoneType] = useState("both"); // "sim" | "esim" | "both"
  const isSim = phoneType === "sim" || phoneType === "both";
  const isEsim = phoneType === "esim" || phoneType === "both";

  return (
    <main className="min-h-screen pb-32 bg-background" data-testid="connected-page">
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn-ghost p-2"
          data-testid="connected-back"
          aria-label="Back"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <div>
          <div className="eyebrow">{t("connected_eyebrow")}</div>
          <h1
            className="mt-1 text-foreground"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, lineHeight: 1.2, fontWeight: 500 }}
            data-testid="connected-headline"
          >
            {t("connected_title")}
          </h1>
          <p className="mt-1 text-foreground/65" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
            {t("connected_subtitle")}
          </p>
        </div>
      </header>

      {/* Phone type selector */}
      <section className="px-5 mt-4 grid grid-cols-2 gap-3" data-testid="connected-phone-selector">
        <PhoneTypeCard
          active={phoneType === "sim"}
          onClick={() => setPhoneType(phoneType === "sim" ? "both" : "sim")}
          icon={<SimCardIcon size={22} />}
          label={t("connected_sim_card")}
          testid="connected-phone-sim"
        />
        <PhoneTypeCard
          active={phoneType === "esim"}
          onClick={() => setPhoneType(phoneType === "esim" ? "both" : "esim")}
          icon={<Smartphone className="w-5 h-5 text-gold" strokeWidth={1.5} />}
          label={t("connected_esim_only")}
          sub={t("connected_esim_only_sub")}
          testid="connected-phone-esim"
        />
      </section>

      {/* Section 2 — Physical SIM */}
      {isSim && (
        <Section testid="connected-sim-section" label={t("connected_sim_label")}>
          <h2 style={headlineStyle}>{t("connected_sim_headline")}</h2>
          <ul className="mt-3 space-y-2.5">
            <Bullet>{t("connected_sim_step_1")}</Bullet>
            <Bullet>{t("connected_sim_step_2")}</Bullet>
            <Bullet>{t("connected_sim_step_3")}</Bullet>
          </ul>
          <AmberCard data-testid="connected-sim-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.6} />
            <span>{t("connected_sim_warning")}</span>
          </AmberCard>
          <Note label={t("connected_sim_cost_label")} value={t("connected_sim_cost")} />
          <Note label={t("connected_sim_compat_label")} value={t("connected_sim_compat")} />
        </Section>
      )}

      {/* Section 3 — eSIM */}
      {isEsim && (
        <Section testid="connected-esim-section" label={t("connected_esim_label")}>
          <AmberCard data-testid="connected-esim-critical-warning" critical>
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.6} />
            <span><strong>{t("connected_esim_critical_strong")}</strong> — {t("connected_esim_critical_body")}</span>
          </AmberCard>
          <div className="space-y-3 mt-4">
            {ESIM_PROVIDERS.map((p) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`connected-esim-provider-${p.name.toLowerCase()}`}
                className="block lux-card p-4 flex items-start gap-3 hover:bg-white/[0.03] transition-colors"
                style={{ minHeight: 88 }}
              >
                <SimCardIcon size={18} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-foreground" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 16 }}>
                    {p.name}
                    <ExternalLink className="w-3 h-3 text-foreground/40" strokeWidth={1.5} />
                  </div>
                  <div className="text-[13px] text-foreground/70 leading-relaxed mt-1">{lang === "es" ? p.descEs : p.desc}</div>
                  <div className="text-gold mt-2" style={{ fontSize: 12, fontWeight: 500 }}>{t("connected_get_esim")}</div>
                </div>
              </a>
            ))}
          </div>
          <Note label={t("connected_esim_note_label")} value={t("connected_esim_note")} />
          <Note label={t("connected_esim_tip_label")} value={t("connected_esim_tip")} />
        </Section>
      )}

      {/* Section 4 — Good to know */}
      <Section testid="connected-tips-section" label={t("connected_tips_label")}>
        <ul className="space-y-3 mt-2">
          {[1, 2, 3, 4].map((i) => (
            <li key={i} className="flex items-start gap-2.5" data-testid={`connected-tip-${i}`}>
              <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" strokeWidth={1.8} />
              <span style={{ color: "#D8D2BF", fontSize: 14, lineHeight: 1.6 }}>{t(`connected_tip_${i}`)}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Section 5 — Bottom quick links */}
      <section className="px-5 mt-8 space-y-3 pb-6" data-testid="connected-quick-links">
        <Link
          to="/settings"
          data-testid="connected-download-offline"
          className="btn-outline w-full justify-center"
          style={{ minHeight: 52 }}
        >
          <Download className="w-4 h-4" strokeWidth={1.5} /> {t("connected_download_offline")}
        </Link>
        <Link
          to="/tips"
          data-testid="connected-view-tips"
          className="btn-outline w-full justify-center"
          style={{ minHeight: 52 }}
        >
          <BookOpen className="w-4 h-4" strokeWidth={1.5} /> {t("connected_view_tips")}
        </Link>
      </section>

      <BottomNav />
      <LegalFooter force />
    </main>
  );
}

const headlineStyle = {
  fontFamily: "'Playfair Display', serif",
  fontSize: 22,
  lineHeight: 1.25,
  color: "#F5F1E8",
};

function Section({ label, children, testid }) {
  return (
    <section className="px-5 mt-7" data-testid={testid}>
      <div
        className="mb-2"
        style={{
          color: "#E8B84B",
          fontSize: 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {label}
      </div>
      {children}
    </section>
  );
}

function PhoneTypeCard({ icon, label, sub, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="lux-card p-4 text-left flex flex-col items-start gap-1.5"
      style={{
        minHeight: 96,
        background: active ? "rgba(232,184,75,0.12)" : "#171717",
        border: active ? "1px solid #E8B84B" : "1px solid rgba(232,184,75,0.25)",
        borderBottom: active ? "2px solid #E8B84B" : "2px solid rgba(232,184,75,0.45)",
        borderRadius: 11,
      }}
      aria-pressed={active}
    >
      <div>{icon}</div>
      <div style={{ color: active ? "#E8B84B" : "#F5F1E8", fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 14, lineHeight: 1.3 }}>
        {label}
      </div>
      {sub && (
        <div style={{ color: "#B0AAA3", fontSize: 10, lineHeight: 1.35 }}>{sub}</div>
      )}
    </button>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" strokeWidth={1.8} />
      <span style={{ color: "#D8D2BF", fontSize: 14, lineHeight: 1.6 }}>{children}</span>
    </li>
  );
}

function AmberCard({ children, critical = false, ...rest }) {
  return (
    <div
      className="mt-4 flex items-start gap-2 px-3 py-2.5"
      style={{
        background: critical ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.05)",
        border: critical ? "1px solid rgba(245,158,11,0.55)" : "1px solid rgba(245,158,11,0.35)",
        borderRadius: 10,
        color: critical ? "#FCD34D" : "#FBBF24",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 13,
        lineHeight: 1.55,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

function Note({ label, value }) {
  return (
    <div className="mt-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <span style={{ color: "#B0AAA3", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", marginRight: 8 }}>{label}:</span>
      <span style={{ color: "#D8D2BF", fontSize: 14, lineHeight: 1.55 }}>{value}</span>
    </div>
  );
}
